"use client"

import { useEffect, useState } from "react"
import { useMusicContext } from "@/context/musicContext"
import convertTime from "@/core/functions/convertTime"
import TextRegularM from "@/components/text/text-regular-m"
import RangeSlider from "./rangeSlider"
import RadioLiveBar from "./radioLiveBar"

type Props = {
  variant?: "footer" | "sidebar"
  currentTime: number
  duration: number
  handleTrackClick: (position: number) => void
}

export default function MusicProgressBar({
  variant = "footer",
  currentTime: externalCurrentTime,
  duration: externalDuration,
  handleTrackClick,
}: Props) {

  const {
  audioRef,
  currentTrackData,
} = useMusicContext()

const isRadio =
  currentTrackData?.type === "radio"


  //const [showRemaining, setShowRemaining] = useState(false)
  const [showRemaining, setShowRemaining] = useState(false)

  useEffect(() => {
    const saved = localStorage.getItem("music-time-mode")
    setShowRemaining(saved === "remaining")
  }, [])
  const [isWaiting, setIsWaiting] = useState(false)

  const [localTime, setLocalTime] = useState(0)
  const [localDuration, setLocalDuration] = useState(1)

  const currentTime = externalCurrentTime ?? localTime
  const duration = externalDuration ?? localDuration


  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const sync = () => {
      setLocalTime(audio.currentTime || 0)
      setLocalDuration(audio.duration || 1)
    }

    const waiting = () => setIsWaiting(true)
    const playing = () => setIsWaiting(false)

    sync()

    audio.addEventListener("timeupdate", sync)
    audio.addEventListener("loadedmetadata", sync)
    audio.addEventListener("durationchange", sync)
    audio.addEventListener("seeked", sync)

    audio.addEventListener("waiting", waiting)
    audio.addEventListener("playing", playing)
    audio.addEventListener("canplay", playing)
    audio.addEventListener("canplaythrough", playing)

    return () => {
      audio.removeEventListener("timeupdate", sync)
      audio.removeEventListener("loadedmetadata", sync)
      audio.removeEventListener("durationchange", sync)
      audio.removeEventListener("seeked", sync)

      audio.removeEventListener("waiting", waiting)
      audio.removeEventListener("playing", playing)
      audio.removeEventListener("canplay", playing)
      audio.removeEventListener("canplaythrough", playing)
    }

  }, [audioRef])


  const handleSeek = (value:number) => {
    const audio = audioRef.current

    if (!audio) return

    audio.currentTime = value
    handleTrackClick(value)
  }


  const compact = variant === "sidebar"


  return (
    <div
      className={`
        flex w-full items-center text-white justify-center
        ${compact ? "gap-1 px-1" : "gap-2 px-0"}
      `}
    >

      {/* TIEMPO ACTUAL */}
      <span className="w-10 text-[10px] text-white/70">
        <TextRegularM>
          {convertTime(currentTime)}
        </TextRegularM>
      </span>


      {/* BARRA */}
      <div
        className="
          relative
          flex-1
          cursor-pointer
          py-2
        "
      >
        <RangeSlider
          value={currentTime}
          minvalue={0}
          maxvalue={duration || 1}
          onSeek={handleSeek}
          waiting={isWaiting && currentTime === 0}
        />
      </div>


      {/* TIEMPO TOTAL / RESTANTE */}
      <span
        onClick={() => {
          setShowRemaining(prev => {
            const next = !prev
          
            localStorage.setItem(
              "music-time-mode",
              next ? "remaining" : "total"
            )
          
            return next
          })
        }}
        className="
          w-10
          text-[10px]
          text-white/70
          cursor-pointer
          select-none
          text-right
        "
      >
        <TextRegularM>
          {
            showRemaining
              ? `-${convertTime(
                  Math.max(duration - currentTime, 0)
                )}`
              : convertTime(duration)
          }
        </TextRegularM>
      </span>

    </div>
  )
}